import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { message } from "antd";
import { refreshFinanceData } from "~/services/server";
import { INTENTS, loadCookiesData, getAuthWrapper , getReadyAuthUser } from "~/utils";
import i18n from "~/i18n";
import * as APITypes from "~/API";
import {
  apiClient,
  PropertyFinancePageLoaderResult,
  intentFailureFromApiResponse,
} from "~/services/apiClient";
import type {
  ExpiringLeasesResult,
  OutsideRentMonthLeasesResult,
  RentCollectionResult,
  StayCollectionResult,
} from "~/types/finance";
import { emptyExpiringLeasesResult } from "~/utils/finance/expiringLeases";
import { emptyOutsideRentMonthLeasesResult } from "~/utils/finance/outsideRentMonthLeases";
import { emptyStayCollectionResult } from "~/utils/finance/stayCollection";
import {
  parseFinancePageTab,
  type FinancePageTab,
} from "../../utils/financePageTabs";
import {
  financePeriodCanonicalRedirect,
  resolveFinancePeriodFromUrl,
} from "../../utils/financePeriodParams";

export async function PropertyFinancePageLoader({
  request,
  params,
}: LoaderFunctionArgs): Promise<PropertyFinancePageLoaderType | Response> {
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user?.uid) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;
  const requestUrl = new URL(request.url);
  const propertyID = params.propertyID || "";
  const tab = parseFinancePageTab(requestUrl.searchParams.get("tab"));
  const { year: displayYear, month: displayMonth, needsCanonicalRedirect } =
    resolveFinancePeriodFromUrl(requestUrl);

  if (needsCanonicalRedirect) {
    return financePeriodCanonicalRedirect(requestUrl, displayYear, displayMonth);
  }

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const result = await apiClient.loader<PropertyFinancePageLoaderResult>(
    "property-finance-page",
    {
      id: propertyID,
      ownerID,
      ownerType,
      userID: user.uid,
      year: String(displayYear),
      month: String(displayMonth),
    }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load property finance page:", result.error);
    if (result.debugLogs?.length) {
      console.error(
        "[property-finance-page debugLogs]\n",
        result.debugLogs.map((line) => `  ${line}`).join("\n"),
      );
    }
    throw new Error(
      result.error ?? "Failed to load property finance page"
    );
  }

  if (result.debugLogs?.length && process.env.NODE_ENV !== "production") {
    console.info(
      "[property-finance-page debugLogs]\n",
      result.debugLogs.map((line) => `  ${line}`).join("\n"),
    );
  }

  const data = result.data;
  const canCreateReport = isAdminOrSuperAdmin || data.canCreateReport;
  const canDeleteReport = isAdminOrSuperAdmin || data.canDeleteReport;

  return {
    propertyDetails: data.propertyDetails,
    financeStatusMonthlyList: data.financeStatusMonthlyList,
    financeStatusSummary: data.financeStatusSummary,
    currentYear: displayYear,
    currentMonth: displayMonth,
    canCreateReport,
    canDeleteReport,
    canReadProperty: data.canReadProperty,
    isAdminOrSuperAdmin,
    subscriptionPlan: data.subscriptionPlan,
    hasFinanceAccess: data.hasFinanceAccess,
    rentCollection: data.rentCollection ?? {
      neverPaid: [],
      inProgress: [],
      counts: { neverPaid: 0, inProgress: 0 },
    },
    stayCollection: data.stayCollection ?? emptyStayCollectionResult(),
    expiringLeases: data.expiringLeases ?? emptyExpiringLeasesResult(),
    outsideRentMonthLeases:
      data.outsideRentMonthLeases ?? emptyOutsideRentMonthLeasesResult(),
    tab,
    ...(result.debugLogs?.length ? { debugLogs: result.debugLogs } : {}),
  };
}

export type PropertyFinancePageLoaderType = {
  propertyDetails: APITypes.Property;
  financeStatusSummary?: APITypes.YearlyStatusSummary;
  financeStatusMonthlyList: APITypes.MonthlyStatusReport[];
  currentYear: number;
  currentMonth: number;
  canCreateReport: boolean;
  canDeleteReport: boolean;
  canReadProperty: boolean;
  isAdminOrSuperAdmin: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasFinanceAccess: boolean;
  rentCollection: RentCollectionResult;
  stayCollection: StayCollectionResult;
  expiringLeases: ExpiringLeasesResult;
  outsideRentMonthLeases: OutsideRentMonthLeasesResult;
  tab: FinancePageTab;
  /** Lambda diagnostic lines (successWithDebugLogs); present in non-prod / staging tests. */
  debugLogs?: string[];
};

const getNumericRequestValue = (
  formData: FormData,
  searchParams: URLSearchParams,
  key: "year" | "month",
): number => {
  const searchValue = searchParams.get(key);
  const parsedSearchValue =
    searchValue == null ? NaN : Number(searchValue);
  if (Number.isFinite(parsedSearchValue)) {
    return parsedSearchValue;
  }

  const formValue = formData.get(key);
  const parsedFormValue =
    typeof formValue === "string" ? Number(formValue) : NaN;
  if (Number.isFinite(parsedFormValue)) {
    return parsedFormValue;
  }

  return 0;
};

const handleRefreshIntent = async (ownerID: string) => {
  const date = new Date();
  return refreshFinanceData(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    "user",
    ownerID,
    false
  );
};

export async function PropertyFinancePageAction({
  request,
  params,
}: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID } = authAccessInfo.data;
  const { searchParams } = new URL(request.url);
  const propertyID = params.propertyID || "";

  const year = getNumericRequestValue(formData, searchParams, "year");
  const month = getNumericRequestValue(formData, searchParams, "month");

  const parseFormBody = (): Record<string, unknown> => {
    const raw = formData.get("body") as string | null;
    if (!raw) return {};
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  };

  switch (intent) {
    case INTENTS._refresh:
      return handleRefreshIntent(ownerID);

    case INTENTS._generate_monthly_finance_report: {
      const result = await apiClient.sendIntent<APITypes.MonthlyStatusReport>(
        INTENTS._generate_monthly_finance_report,
        { year, month, propertyID }
      );
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to generate monthly report",
        );
        message.error(failure.error);
        return failure;
      }
      message.success(i18n.t("Form_submitted_successfully"));
      return result.data;
    }

    case INTENTS._generate_yearly_finance_report: {
      const result = await apiClient.sendIntent<APITypes.YearlyStatusSummary>(
        INTENTS._generate_yearly_finance_report,
        { year, propertyID }
      );
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to generate yearly report",
        );
        message.error(failure.error);
        return failure;
      }
      message.success(i18n.t("Form_submitted_successfully"));
      return result.data;
    }

    case INTENTS._delete_monthly_finance_report: {
      const payload = parseFormBody();
      const result = await apiClient.sendIntent<{ message: string }>(
        INTENTS._delete_monthly_finance_report,
        { year, month, propertyID, ...payload }
      );
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to delete monthly report",
        );
        message.error(failure.error);
        return failure;
      }
      message.success(result.data.message);
      return { success: true, ...result.data };
    }

    case INTENTS._delete_yearly_finance_report: {
      const payload = parseFormBody();
      const result = await apiClient.sendIntent<{ message: string }>(
        INTENTS._delete_yearly_finance_report,
        { year, month, propertyID, ...payload }
      );
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to delete yearly report",
        );
        message.error(failure.error);
        return failure;
      }
      message.success(result.data.message);
      return { success: true, ...result.data };
    }

    case INTENTS._bulk_request_rent_payments: {
      const result = await apiClient.sendIntent<{
        created: APITypes.Transaction[];
        skipped: Array<{
          leaseID: string;
          unitID: string;
          reason: string;
        }>;
        summary: {
          candidateCount: number;
          createdCount: number;
          skippedCount: number;
        };
      }>(INTENTS._bulk_request_rent_payments, {
        year,
        month,
        propertyID,
      });
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to create rent payment requests",
        );
        message.error(failure.error);
        return failure;
      }
      return result.data;
    }

    case INTENTS._bulk_request_stay_payments: {
      const result = await apiClient.sendIntent<{
        created: APITypes.Transaction[];
        skipped: Array<{
          stayID: string;
          unitID: string;
          reason: string;
        }>;
        summary: {
          candidateCount: number;
          createdCount: number;
          skippedCount: number;
        };
      }>(INTENTS._bulk_request_stay_payments, {
        year,
        month,
        propertyID,
      });
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to create stay payment requests",
        );
        message.error(failure.error);
        return failure;
      }
      return result.data;
    }
  }
  return {};
}
